import { match } from 'ts-pattern';
import { BigIntWithDecimals, USD_DECIMALS, LendingMerklRewardLegacy, getChecksumAddressOrThrow } from '@silo/types';
import { calculateRewardsApr } from '@silo/contract-utils';
import { MerklCampaign } from '@silo/data';

interface ToLendingMerklRewardLegacyParams {
  campaign: MerklCampaign;
  totalCollateral: bigint;
  totalProtected: bigint;
  totalDebt: bigint;
  symbol: string;
  decimals: number;
  tokenPriceUsd: BigIntWithDecimals;
}

export function toLendingMerklRewardLegacy({
  campaign,
  totalCollateral,
  totalProtected,
  totalDebt,
  symbol,
  decimals,
  tokenPriceUsd,
}: ToLendingMerklRewardLegacyParams): LendingMerklRewardLegacy {
  const {
    rewardTokenSymbol,
    rewardTokenDecimals,
    merklApr,
    amountPerSecond,
    positionType: shareTokenType,
    isSuperFest,
  } = campaign;

  const targetTokenAddress = getChecksumAddressOrThrow(campaign.tokenAddress);
  const rewardTokenAddress = getChecksumAddressOrThrow(campaign.rewardTokenAddress);
  const rewardTokenPriceUsd = campaign.rewardTokenPriceUsd ?? 0n;

  const totalTokens = match(shareTokenType)
    .with('collateral', () => totalCollateral)
    .with('protected', () => totalProtected)
    .with('debt', () => totalDebt)
    .exhaustive();

  const calculatedApr = calculateRewardsApr({
    emissionPerSecond: amountPerSecond,
    rewardTokenPriceUsd: {
      value: rewardTokenPriceUsd,
      decimals: USD_DECIMALS,
    },
    rewardTokenDecimals,
    totalTokens: {
      value: totalTokens,
      decimals,
    },
    tokenPriceUsd,
  });

  const lendingMerklRewardLegacy: LendingMerklRewardLegacy = {
    targetTokenAddress,
    targetTokenSymbol: symbol,
    rewardTokenAddress,
    rewardTokenSymbol,
    rewardTokenDecimals,
    rewardTokenPriceUsd,
    merklApr,
    calculatedApr,
    apr: merklApr ?? calculatedApr,
    isSuperFest: isSuperFest,
    isSuperfest: isSuperFest,
    positionType: shareTokenType,
    amountPerSecond,
    totalTokens,

    tokenAddress: targetTokenAddress,
    symbol: rewardTokenSymbol,
    type: shareTokenType,
  };

  return lendingMerklRewardLegacy;
}
